import React, { FC, useState, useEffect, useMemo, useRef } from "react";
import { Select, Collapse, Checkbox, Modal, Button, notification } from "antd";
import { sortBy, find, get, concat, map, cloneDeep, difference, isEmpty, includes, forEach, List } from "lodash";
import moment from "moment";

import "./referrals.scss";
import { ReferralsProvider, useReferralsContext, useUpdateAttribute } from "./useReferrals";
import { Spinner } from "@components/healthemSpinner/healthemSpinner";
import SimpleSelect from "@components/simpleSelect/simpleSelect";
import ModalCustom from "@components/modal-custom";
import { NoteModal } from "./Modal";
import { ACTIVE, COMPLETE, INPROGRESS, NOT_APPLICABLE, NOTE, CONFIRMATION, NOT_ASSIGNED, TRIGGERED } from "@utils/constants";
import { useDropdowns } from "@utils/customHooks";
import { useReferralsStore } from "@stores/referralsStore";
import { useProgramMetaData } from "@pages/member-details/care-coordination/customHooks";
import membersService from "@services/members.service";
import { useMemberDetailsStore } from "@stores/memberDetailsStore";
import memberDetailsService from "@services/memberDetails.service";
import { useCareCoordinationStore } from "@stores/useCareCoordinationStore";
import { CanEdit } from "@components/accessLevel";

const { Panel } = Collapse;
const { Option } = Select;

export const mappingOfCMRole: any = [];
let palliativeProgramCheckRole: any = {};

function StatusDropdown({ assignee, assigne_id, status, handleChange, role_name, handleChangeForAssigneeAndStatus }: any) {
  console.log("Referrals.StatusDropdown");

  const { filterDropdowns } = useDropdowns();
  const primaryUserRole = useReferralsStore((state) => state.primaryUserRole);

  const _assignee = primaryUserRole === "CareManager" && assignee === null ? membersService.getCurrentMember.caremgr_name : assignee;
  const _assignee_id = primaryUserRole === "CareManager" && assigne_id === null ? membersService.getCurrentMember.care_manager_id : assigne_id;
  const isDisabled = ({ status, value }: { status: string; value: string }) => {
    // on load, if the case manager assigne is null but the role is case manager, then get it from member list

    if (primaryUserRole === "CareManager" && role_name === "Case Manager" && !isEmpty(_assignee)) {
      return false;
    } else {
      if (status === NOT_APPLICABLE || value === NOT_APPLICABLE) {
        return true;
      } else if (status === ACTIVE && value === COMPLETE) {
        return true;
      }
    }
    return false;
  };

  return (
    <div
      className={`ui-care-assignment-body__status ${
        status === COMPLETE ? "done" : status === NOT_APPLICABLE ? "notApplicable" : status === ACTIVE ? "active" : status === INPROGRESS ? "inProgress" : status === TRIGGERED ? "triggered" : "done"
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <Select
        size="small"
        value={status}
        onClick={(e) => e.stopPropagation()}
        onChange={(status: any) => {
          if (primaryUserRole === "CareManager" && role_name === "Case Manager" && isEmpty(assignee) && !isEmpty(_assignee)) {
            handleChangeForAssigneeAndStatus({
              status: { status_val: status },
              assignee: {
                assignee_id: _assignee_id,
                assignee_val: _assignee,
                role_id: 3, // this should be dynamic, for a quick fix I am writng it as static value.
              },
            });
          } else {
            handleChange({ status_val: status });
          }
        }}
      >
        {get(filterDropdowns, "CareTeamStaticWorkStatusLst", []).map((item: any) => (
          <Option key={item.value} value={item.value} disabled={isDisabled({ status, value: item.value })}>
            {item.label}
          </Option>
        ))}
      </Select>
    </div>
  );
}

function StatusInfo({ status }: any) {
  console.log("Referrals.StatusInfo");
  return status && status !== "null" ? (
    <div
      className={`ui-care-assignment-body__status ${
        status === COMPLETE ? "done" : status === NOT_APPLICABLE ? "notApplicable" : status === ACTIVE ? "active" : status === INPROGRESS ? "inProgress" : status === TRIGGERED ? "triggered" : "done"
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <Select size="small" className={`ui-care-assignment-body__status-info`} open={false} value={status === "NA" ? "Not Applicable" : status} onClick={(e) => e.stopPropagation()}></Select>
    </div>
  ) : (
    <></>
  );
}

function WorkStatus({ referral, handleChange, handleChangeForAssigneeAndStatus }: any) {
  console.log("Referrals.WorkStatus");
  const { status, type, order, assignee, role_name, assigne_id } = referral;

  return type === "dropdown" ? (
    <CanEdit hide={false} wrapperProps={{style: {width: '100%', height: '100%'}}} innerProps={{style: {width: '100%', height: '100%'}}}>
    <StatusDropdown
      assignee={assignee}
      assigne_id={assigne_id}
      role_name={role_name}
      status={status}
      handleChange={(status: string) => handleChange({ status, order })}
      handleChangeForAssigneeAndStatus={(status: any) => {
        handleChangeForAssigneeAndStatus({ ...status, order });
      }}
    />
    </CanEdit>
  ) : type === "program" ? (
    <StatusInfo status={status} />
  ) : (
    <></>
  );
}

function MemberInfo({ value }: any) {
  console.log("Referrals.MemberInfo");
  return <div className={`ui-care-assignment-body__member-info`}>{value}</div>;
}

function Member({ referral, handleChange }: any) {
  console.log("Referrals.Member");
  const { type, value, order, assignee, role_name } = referral;

  const { loadingOptions, filterDropdowns } = useDropdowns();
  const primaryUserRole = useReferralsStore((state) => state.primaryUserRole);

  // on load, if the case manager assigne is null but the role is case manager, then get it from member list
  const _assignee = primaryUserRole === "CareManager" && assignee === null ? membersService.getCurrentMember.caremgr_name : assignee;
  const options = useMemo(() => {
    const _data = get(filterDropdowns, value, []);
    return concat(_data, {
      value: null,
      label: NOT_ASSIGNED,
      title: NOT_ASSIGNED,
      key: null,
      additional_info: get(_data, `[0].additional_info`, null),
      // if selected role is care manager, then person can not choose unAssigned and same for Palliative
      disabled: primaryUserRole && primaryUserRole === get(find(mappingOfCMRole, { roleLabelForReferral: role_name }), "roleId") ? true : false,
    });
  }, [filterDropdowns, primaryUserRole, role_name, value]);

  const onDropdownChange = (assignee_id: string) => {
    handleChange({
      assignee: {
        assignee_id: assignee_id,
        assignee_val: find(options, { value: assignee_id })?.title,
        role_id: find(options, { value: assignee_id })?.additional_info,
      },
      order,
    });
  };

  const listOfCanBeDisabledRoles = map(mappingOfCMRole, (item) => {
    return item.roleLabelForReferral;
  });
  const activeRoleLabelForReferral = find(mappingOfCMRole, { roleId: primaryUserRole })?.roleLabelForReferral;
  const disabled = includes(listOfCanBeDisabledRoles, role_name) ? activeRoleLabelForReferral !== role_name && !isEmpty(assignee) : false;

  return type === "dropdown" ? (
    <div
      onClick={(e) => {
        // getOptions(value);
        e.stopPropagation();
      }}
    >
      <CanEdit hide={false}>
      <SimpleSelect
        handleSelect={(assignee_id: any) => onDropdownChange(assignee_id)}
        loadingOptions={loadingOptions}
        value={find(options, { title: _assignee })?.value || null}
        options={options}
        disabled={disabled && !isEmpty(find(options, { title: _assignee })?.value)}
      />
      </CanEdit>
    </div>
  ) : (
    <MemberInfo value={referral.assignee} />
  );
}

function ReferralHeading({ type, heading }: { type: string; heading: string | null }) {
  console.log("Referrals.ReferralHeading");
  return (
    <>
      {type !== "EDIT" ? <div className="ui-care-assignment-seperator"></div> : <></>}
      <div className="ui-care-assignment-heading">{heading}</div>
      <div className="ui-care-assignment-subheading">
        <div className="ui-care-assignment-subheading__role">Role Name</div>
        <div className="ui-care-assignment-subheading__member">Care Team Member</div>
        <div className="ui-care-assignment-subheading__status">{type !== "EDIT" ? "Care Program Status" : "Work Status"}</div>
      </div>
    </>
  );
}

function ReferralBody({ referral }: any) {
  console.log("Referrals.ReferralBody");
  const { role_name, order, status: prev_status, assignee: prev_assignee, value } = referral;
  const { filterDropdowns } = useDropdowns();
  const { updateReferrals, isReferralUpdating, setShowModal } = useReferralsContext();
  const primaryUserRole: any = useReferralsStore((state) => state.primaryUserRole);
  const tempValueOfRole: any = useRef(null);
  tempValueOfRole.current = primaryUserRole;
  const { updatePrimaryUser } = useUpdateAttribute(tempValueOfRole);

  function handleChange({ status, assignee, order }: any) {
    console.log("ReferralBody.handleChange");
    if (order !== undefined && (status?.status_val || assignee?.assignee_val)) {
      const obj: any = { order }; // Initialize obj with the 'order' property

      if (status) {
        obj.status = { status_val: status.status_val };
        // when work status is changed we need the role_id update in db which we get from the member role dropdown
        obj.assignee = { role_id: get(get(filterDropdowns, `${value}`, [])[0], "additional_info", 0) };
        if (prev_status === INPROGRESS && status?.status_val === ACTIVE) {
          // if previous work status is in inprogress and we changed to To do we need to show popup and get confirmation from user
          setShowModal({
            title: "Work Status Change",
            body: `Are you sure you want to change the Work Status from “In Progress” to “To Do” `,
            type: CONFIRMATION,
            onOk: () => {
              setShowModal({});
              updateReferrals(obj);
            },
          });
        } else if (status?.status_val === COMPLETE) {
          // if work status changed to Done we need to show popup so user can add a note
          setShowModal({
            title: "Work Status – Done",
            body: ``,
            type: NOTE,
            onOk: (noteContent: string) => {
              setShowModal({});
              updateReferrals({ ...obj, noteContent });
            },
          });
        } else {
          updateReferrals(obj);
        }
      }

      if (assignee) {
        obj.assignee = {
          assignee_val: assignee.assignee_val,
          assignee_id: assignee.assignee_id,
          role_id: assignee.role_id,
        };

        if ((!prev_assignee || prev_assignee === NOT_ASSIGNED) && prev_status === NOT_APPLICABLE) {
          // if previous assignee is not assigned and work status is in not applicable we need to change the work status to To do on change of member role
          obj.status = { status_val: ACTIVE };
        } else if (!assignee.assignee_id) {
          // if assignee is changed to not assigned we need to change the work status to not applicable
          obj.status = { status_val: NOT_APPLICABLE };
        } else {
          obj.status = { status_val: referral?.status === NOT_APPLICABLE ? ACTIVE : referral?.status };
        }
        setShowModal({
          title: "Care Team Member Change",
          body: `Are you sure you want to change the Care Team Member`,
          type: CONFIRMATION,
          onOk: () => {
            setShowModal({});
            updateReferrals({
              ...obj,
              callback: () => {
                if (assignee.role_id === get(find(mappingOfCMRole, { roleId: primaryUserRole }), "rollNumber")) {
                  updatePrimaryUser();
                }
              },
            });
          },
        });
      }
    }
  }
  function handleChangeForAssigneeAndStatus({ status, assignee, order }: any) {
    console.log("ReferralBody.handleChange");
    if (order !== undefined && (status?.status_val || assignee?.assignee_val)) {
      const obj: any = { order }; // Initialize obj with the 'order' property
      if (assignee) {
        obj.assignee = {
          assignee_val: assignee.assignee_val,
          assignee_id: assignee.assignee_id,
          role_id: assignee.role_id,
        };

        if ((!prev_assignee || prev_assignee === NOT_ASSIGNED) && prev_status === NOT_APPLICABLE) {
          // if previous assignee is not assigned and work status is in not applicable we need to change the work status to To do on change of member role
          obj.status = { status_val: ACTIVE };
        } else if (!assignee.assignee_id) {
          // if assignee is changed to not assigned we need to change the work status to not applicable
          obj.status = { status_val: NOT_APPLICABLE };
        }
        setShowModal({
          title: "Care Team Member Change",
          body: `Are you sure you want to change the Care Team Member`,
          type: CONFIRMATION,
          onOk: () => {
            setShowModal({});
            updateReferrals({
              ...obj,
              // callback: () => {
              //   if (assignee.role_id === get(find(mappingOfCMRole, { roleId: primaryUserRole }), "rollNumber")) {
              //     updatePrimaryUser();
              //   }
              // },
            });

            if (status) {
              obj.status = { status_val: status.status_val };
              // when work status is changed we need the role_id update in db which we get from the member role dropdown
              obj.assignee = { role_id: get(get(filterDropdowns, `${value}`, [])[0], "additional_info", 0) };
              if (prev_status === INPROGRESS && status?.status_val === ACTIVE) {
                // if previous work status is in inprogress and we changed to To do we need to show popup and get confirmation from user
                setShowModal({
                  title: "Work Status Change",
                  body: `Are you sure you want to change the Work Status from “In Progress” to “To Do” `,
                  type: CONFIRMATION,
                  onOk: () => {
                    setShowModal({});
                    updateReferrals(obj);
                  },
                });
              } else if (status?.status_val === COMPLETE) {
                // if work status changed to Done we need to show popup so user can add a note
                setShowModal({
                  title: "Work Status – Done",
                  body: ``,
                  type: NOTE,
                  onOk: (noteContent: string) => {
                    setShowModal({});
                    updateReferrals({ ...obj, noteContent });
                  },
                });
              } else {
                updateReferrals(obj);
              }
            }
          },
        });
      }
    }
  }

  return (
    <Spinner loading={order === isReferralUpdating} size={45}>
      <div className="ui-care-assignment-body">
        <div className="ui-care-assignment-body__role">{role_name}</div>
        <div className="ui-care-assignment-body__member">{<Member referral={referral} handleChange={handleChange} />}</div>
        <div>{<WorkStatus referral={referral} handleChange={handleChange} handleChangeForAssigneeAndStatus={handleChangeForAssigneeAndStatus} />}</div>
      </div>
    </Spinner>
  );
}

function PrimaryUserRoleDropDown({ primaryUserRoleList }: any) {
  const store = useReferralsStore((store) => store);

  // const [primaryUserRole, setPrimaryUserRole] = useState(null);
  const primaryUserRole = useReferralsStore((state) => state.primaryUserRole);
  const memberData = useMemberDetailsStore((state) => state.memberData);
  const currentCmModalVisibility = useReferralsStore((state) => state.currentCmModalVisibility);
  const setCurrentCmModalVisibility = useReferralsStore((state) => state.setCurrentCmModalVisibility);

  const tempValueOfRole: any = useRef(null);

  const { updatePrimaryUser, showLoader } = useUpdateAttribute(tempValueOfRole);
  const assignedPrograms = useCareCoordinationStore((state) => state.assignedPrograms);

  return (
    <>
      <div className="ui-care-assignment-heading">Primary User Role</div>
      {/* this dropdown needs to disabled if palliative worker is not selected */}
      <CanEdit hide={false}>
      <Select
        // disabled={isEmpty(get(find(get(store, "referrals.editable", {}), { role_name: "Palliative Care Worker" }), "assigne_id", ""))}
        placeholder="Select Role"
        style={{ width: 200 }}
        size="large"
        onClick={(e) => e.stopPropagation()}
        value={primaryUserRole}
        key={`${tempValueOfRole.current}`}
        onChange={(value: any) => {
          // if we are trying to set the care manager as primary role, then palliative program should be closed.
          if (primaryUserRole === palliativeProgramCheckRole?.roleId && get(find(assignedPrograms, { description: "PALLIATIVE CARE" }), "status", null) === "Active") {
            notification.info({
              message: "Info",
              description: "Please complete the active Palliative program.",
            });
            return;
          }

          setCurrentCmModalVisibility(true);
          tempValueOfRole.current = value;
        }}
        className="ui-care-assignment-primary-user-dropdown"
      >
        {primaryUserRoleList.map((item: any) => (
          <Option
            key={item.value}
            value={item.value}
            disabled={isEmpty(get(find(get(store, "referrals.editable", {}), { role_name: get(find(mappingOfCMRole, { roleId: item.value }), "roleLabelForReferral", null) }), "assigne_id", ""))}
          >
            {item.label}
          </Option>
        ))}
      </Select>
      </CanEdit>

      <Modal
        open={currentCmModalVisibility}
        footer={null}
        onCancel={() => {
          setCurrentCmModalVisibility(false);
          tempValueOfRole.current = null;
        }}
      >
        <div className="currentCmChangeModal">
          <div className="currentCmChangeModal__header">{`Do you want to update ${get(
            find(get(store, "referrals.editable", {}), { role_name: get(find(mappingOfCMRole, { roleId: tempValueOfRole?.current }), "roleLabelForReferral", null) }),
            "assignee",
            null
          )} as Primary User for ${membersService?.getCurrentMember?.patient_name} ?`}</div>
          <div className="currentCmChangeModal__body">Primary User update will reflect in 24 hours</div>
          <div className="currentCmChangeModal__footer">
            <div className="currentCmChangeModal__footer__cancel">
              <Button
                onClick={() => {
                  setCurrentCmModalVisibility(false);
                  tempValueOfRole.current = null;
                }}
              >
                Cancel
              </Button>
            </div>
            <div className="currentCmChangeModal__footer__ok">
              <Button type="primary" onClick={() => updatePrimaryUser()} loading={showLoader}>{`Yes, Update`}</Button>
            </div>
          </div>
        </div>
      </Modal>
      {memberData?.attribCareManagerAssignmentStatus === "N" ? <div>Pending reassignment, will be updated in 24 hours.</div> : null}
      <div className="ui-care-assignment-seperator"></div>
    </>
  );
}

function ReferralCard({ referrals, type }: any) {
  console.log("Referrals.ReferralCard");
  return (
    <div className="ui-care-assignment">
      <ReferralHeading type={type} heading={referrals[0].heading} />
      {referrals.map((r: any) => (
        <ReferralBody referral={r} />
      ))}
    </div>
  );
}

const Referrals: FC<any> = ({ primaryUserRoleList }) => {
  const {
    referrals: { editable, nonEditable },
    loading,
  } = useReferralsContext();

  const { loadingOptions, getOptions } = useDropdowns();

  useEffect(() => {
    getOptions("CareTeamStaticWorkStatusLst");
  }, [getOptions]);

  useEffect(() => {
    if (editable) {
      const valuesArray: any = map(editable, "value");
      getOptions(valuesArray, "ReferralsUsersList");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable]);

  const data = useMemo(() => {
    console.log("Referrals.useMemo");
    if (editable && nonEditable) {
      return { editable: sortBy(editable, [(item) => item.role_name.toLowerCase()]), nonEditable: sortBy(nonEditable, [(item) => item.role_name.toLowerCase()]) };
    }
    return { editable: [], nonEditable: [] };
  }, [editable, nonEditable]);

  return (
    <Spinner loading={loading || loadingOptions} size={70}>
      <PrimaryUserRoleDropDown primaryUserRoleList={primaryUserRoleList} />
      {get(data, "editable", []).length > 0 ? <ReferralCard referrals={data?.editable} type={"EDIT"} /> : <></>}
      {get(data, "nonEditable", []).length > 0 ? <ReferralCard referrals={data?.nonEditable} type={"NON-EDIT"} /> : <></>}
    </Spinner>
  );
};

function Decline() {
  const { declineRequiredPrograms } = useProgramMetaData({});

  const [checkboxPrograms, setCheckboxCarePrograms] = useState<any>(declineRequiredPrograms.filter((p: any) => p.isDeclined).map((program: any) => program.id));

  const { updateProgramDecline, isDeclineStatusUpdating } = useReferralsContext();

  function handleDeclineChange(checkedValues: any[]) {
    const declinedProgramId = difference(checkedValues, checkboxPrograms)[0];

    const unDeclinedProgramId = difference(checkboxPrograms, checkedValues)[0];

    //declinedProgramId will be present if a new program is checked
    if (declinedProgramId) {
      updateProgramDecline({ prgmId: declinedProgramId, isDeclined: "Y" });
    }
    //unDeclinedProgramId will be present if a new program is unchecked
    else if (unDeclinedProgramId) {
      updateProgramDecline({ prgmId: unDeclinedProgramId, isDeclined: "N" });
    }

    setCheckboxCarePrograms(checkedValues);
  }

  const options = useMemo(() => {
    return declineRequiredPrograms
      ?.sort((a: any, b: any) => a?.label.localeCompare(b?.label))
      .map((p: any) => {
        return {
          ...p,
          disabled: p.disableInDecline,
          tooltip: p.isDeclined ? `Decline Record by ${p.declinedBy} on ${moment(p.declinedAt).format("MM-DD-YYYY")}` : "",
          label: <Spinner tooltip={p.tooltip}>{p.label}</Spinner>,
        };
      });
  }, [declineRequiredPrograms]);

  return declineRequiredPrograms.length > 0 ? (
    <Spinner loading={isDeclineStatusUpdating} size={70}>
      <div className="ui-care-assignment">
        <div className="ui-care-assignment-heading">{"Care Program Decline"}</div>
        <div className="ui-care-assignment-decline__body">
          <div className="ui-care-assignment-decline__body__heading">
            Select Care Programs which are Declined<div className="ui-care-assignment-decline__body__heading--sub-heading">(Displays only the latest version of the Care Program)</div>
          </div>
          <div className="ui-care-assignment-decline__body__program">
            <Checkbox.Group value={checkboxPrograms} onChange={(checkedValues) => handleDeclineChange(checkedValues)}>
              {options.map((option: any) => (
                <Spinner tooltip={option.tooltip}>
                  <CanEdit hide={false}>
                  <Checkbox key={option.value} value={option.value} disabled={option.disabled}>
                    {option.label}
                  </Checkbox>
                  </CanEdit>
                </Spinner>
              ))}
            </Checkbox.Group>
          </div>
        </div>
      </div>
      <div className="ui-care-assignment-seperator"></div>
    </Spinner>
  ) : (
    <></>
  );
}

const Referral: FC<any> = ({ primaryUserRoleList }) => {
  const { showModal, setShowModal } = useReferralsContext();

  const { title, body, onOk, type } = showModal;

  return (
    <>
      <Decline />
      <Referrals primaryUserRoleList={primaryUserRoleList} />
      <ModalCustom title={title ?? ""} visible={type === CONFIRMATION} width={500} onOk={() => onOk()} setVisible={() => setShowModal({})} zIndex={2} text={body ?? ""}></ModalCustom>
      <NoteModal open={type === NOTE} title={title} onCancel={() => setShowModal({})} onOk={(noteContent: string) => onOk(noteContent)} />
    </>
  );
};

const Wrapper: React.FC<any> = function ({ activeKey, setActivePanels, dataCardId }: any) {
  const [activeCardPanel, setActiveCardPanel] = useState<any[]>(activeKey?.includes(dataCardId) ? [dataCardId] : []);

  const store = useReferralsStore((store) => store);
  const primaryUserRole = useReferralsStore((state) => state.primaryUserRole);
  const setPrimaryUserRole = useReferralsStore((state) => state.setPrimaryUserRole);
  const [primaryUserRoleList, setPrimaryUserRoleList] = useState([]);

  useEffect(() => {
    console.log("CurrentCM.useEffect");
    memberDetailsService.fetchAttributeDropdown("PrimaryUserRolesLst").subscribe({
      next: (response: any) => {
        setPrimaryUserRoleList(response.data.data.data);
        forEach(response.data.data.data, (item) => {
          mappingOfCMRole.push({
            roleLabel: item.label,
            roleId: item.value,
            roleLabelForReferral: item.label_for_ref,
            rollNumber: item.role_id,
          });

          if (item.label?.toLowerCase().includes("palliative")) {
            palliativeProgramCheckRole = {
              roleLabel: item.label,
              roleId: item.value,
              roleLabelForReferral: item.label_for_ref,
              rollNumber: item.role_id,
            };
          }
        });
        // if we are not getting the role from attribute table, then we need to refer member list.
        setPrimaryUserRole(
          isEmpty(store?.referrals?.primaryUserRoleRelatedData?.roleId)
            ? get(find(mappingOfCMRole, { roleLabel: membersService.getCurrentMember.primary_user_role_description }), "roleId")
            : store?.referrals?.primaryUserRoleRelatedData?.roleId
        );
      },
      error: (error) => {
        console.log(error);
      },
    });

    // if we are not getting the role from attribute table, then we need to refer member list.
    setPrimaryUserRole(
      isEmpty(store?.referrals?.primaryUserRoleRelatedData?.roleId)
        ? get(find(mappingOfCMRole, { roleLabel: membersService.getCurrentMember.primary_user_role_description }), "roleId")
        : store?.referrals?.primaryUserRoleRelatedData?.roleId
    );
  }, [setPrimaryUserRole, store?.referrals?.primaryUserRoleRelatedData?.roleId]);

  useEffect(() => {
    setActiveCardPanel(activeKey?.includes(dataCardId) ? [dataCardId] : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  const onCollapseChange = () => {
    if (activeCardPanel.length > 0) {
      setActivePanels(cloneDeep(activeKey)?.filter((item: any) => item !== dataCardId));
    } else {
      setActivePanels([...(activeKey ? activeKey : []), dataCardId]);
    }
  };

  const updatedCmName =
    primaryUserRole === "CareManager" &&
    get(find(get(store, "referrals.editable", {}), { role_name: get(find(mappingOfCMRole, { roleId: primaryUserRole }), "roleLabelForReferral", null) }), "assignee", null) === null
      ? membersService.getCurrentMember.caremgr_name
      : get(find(get(store, "referrals.editable", {}), { role_name: get(find(mappingOfCMRole, { roleId: primaryUserRole }), "roleLabelForReferral", null) }), "assignee", "Not Assigned");

  return (
    <ReferralsProvider>
      <Collapse accordion={true} activeKey={activeCardPanel} expandIconPosition="right" bordered={false} onChange={onCollapseChange}>
        <Panel
          className="ui-care-plan-customization-tab--panel1"
          header={`Care Team Assignment ${`(${get(store, "referrals.primaryUserRoleRelatedData.heading", "")} - ${updatedCmName})`}`}
          key={dataCardId}
          style={{ marginBottom: "16px" }}
        >
          <Referral primaryUserRoleList={primaryUserRoleList} />
        </Panel>
      </Collapse>
    </ReferralsProvider>
  );
};

export default Wrapper;
